import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';

import type { TimelinePoint } from 'cadenza/api-client/timeline-api';
import { TimelineLayerFadingTransition } from 'cadenza/api-client/timeline-dto';
import { calculateRotation } from 'cadenza/map/style/style-util';
import {
  ARROW_MINIMUM_VISIBILITY_DISTANCE_SQUARED,
  createArrowStyle
} from 'cadenza/map/timeline-visualization/timeline-constants';
import { ArrowCachingProxyStyle } from 'cadenza/map/timeline-visualization/styles/arrow-caching-proxy-style';
import type { FadingSupplierFactory, LayerTimelinePoint } from 'cadenza/map/timeline-visualization/timeline-utilities';
import type { CoordinateDto } from 'cadenza/api-client/dto/coordinate-dto';

import type OlMap from 'ol/Map';

const DEFAULT_FADING = { fadingFactor: 1, fadingTransition: TimelineLayerFadingTransition.NONE };

export function createArrowToPreviousCoordinate (
  previousPoint: TimelinePoint,
  endPoint: TimelinePoint,
  imageCache: Map<string, object>,
  map: OlMap,
  cachedZoom: object,
  fadingSupplier: FadingSupplierFactory = () => () => DEFAULT_FADING) {
  const previousCoordinate = previousPoint.coordinate;
  const coordinate = endPoint.coordinate;
  const arrow = new Feature({
    geometry: new Point(calculateCenter(previousCoordinate, coordinate)),
    time: endPoint.time
  });
  const rotation = calculateRotation(previousCoordinate, coordinate);
  const rotatedStyle = createArrowStyle();
  rotatedStyle.getImage()!.setRotation(rotation);
  arrow.setStyle(new ArrowCachingProxyStyle(
    rotatedStyle,
    fadingSupplier(previousPoint as LayerTimelinePoint, endPoint as LayerTimelinePoint),
    imageCache,
    rotation,
    calculateDistanceSquared(previousCoordinate, coordinate),
    () => isArrowVisible(map, previousCoordinate, coordinate),
    cachedZoom));

  return arrow;
}

function calculateDistanceSquared (c1: CoordinateDto, c2: CoordinateDto) {
  return c1[0] * c2[0] + c1[1] * c2[1];
}

function isArrowVisible (
  map: OlMap,
  startCoordinate: CoordinateDto,
  endCoordinate: CoordinateDto) {
  const pixelCoordinate = map.getPixelFromCoordinate(endCoordinate);
  const previousPixelFromCoordinate = map.getPixelFromCoordinate(startCoordinate);
  const dx = pixelCoordinate[0] - previousPixelFromCoordinate[0];
  const dy = pixelCoordinate[1] - previousPixelFromCoordinate[1];
  const distanceSquared = dx * dx + dy * dy;
  return distanceSquared > ARROW_MINIMUM_VISIBILITY_DISTANCE_SQUARED;
}

function calculateCenter (coordinate1: CoordinateDto, coordinate2: CoordinateDto): CoordinateDto {
  return [
    (coordinate1[0] + coordinate2[0]) / 2,
    (coordinate1[1] + coordinate2[1]) / 2 ];
}
